import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api, { Category } from "../lib/api";
import { useTenant } from "../lib/tenant";

export default function Footer() {
  const { i18n } = useTranslation();
  const isJa = i18n.language.startsWith("ja");
  const { id: tenantId, name: tenantName, tTenant } = useTenant();

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: () => api.get("/products/categories").then((r) => Array.isArray(r.data) ? r.data : []),
  });

  return (
    <footer className="bg-gray-50 border-t border-gray-200 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
          <div>
            <img 
              src={`/branding/${tenantId}/logo.${tenantId === 'chlorine.art' ? 'svg' : 'png'}`} 
              alt={tenantName} 
              className="h-10 w-auto mb-6" 
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (img.src.endsWith('.svg')) {
                  img.src = `/branding/${tenantId}/logo.png`;
                  return;
                }
                img.style.display = 'none';
                const parent = img.parentElement;
                if (parent && !parent.querySelector('.logo-text')) {
                  const span = document.createElement('span');
                  span.className = 'logo-text font-bold text-lg uppercase tracking-widest';
                  span.innerText = tenantName;
                  parent.appendChild(span);
                }
              }}
            />
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">{tTenant("footer.tagline")}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-4">🖨️ Epson SC-PX1V · Tokyo, Japan</p>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-900 mb-6">{tTenant("footer.shop")}</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3">
              {categories.filter((c) => !c.hidden).map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/shop?category=${cat.slug}`}
                    className="text-sm text-gray-500 hover:opacity-70 transition-colors"
                    style={{ color: 'var(--brand-color)' }}
                  >
                    {isJa && cat.nameJa ? cat.nameJa : cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-900 mb-6">{tTenant("footer.company")}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-sm text-gray-500 hover:opacity-70 transition-colors">{tTenant("footer.about")}</Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-gray-500 hover:opacity-70 transition-colors">{isJa ? "よくあるご質問" : "FAQ"}</Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-gray-500 hover:opacity-70 transition-colors">{tTenant("footer.privacy")}</Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-gray-500 hover:opacity-70 transition-colors">{tTenant("footer.terms")}</Link>
              </li>
              <li>
                <Link to="/tokushoho" className="text-sm text-gray-500 hover:opacity-70 transition-colors">
                  {i18n.language.startsWith("ja") ? "特定商取引法" : "Specified Commercial Transactions"}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 border-t border-gray-100 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
            &copy; {new Date().getFullYear()} {tenantName}. All rights reserved.
          </p>
          <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
            {tTenant("footer.love")} <span style={{ color: 'rgb(var(--brand-color))' }}>♥</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
